import React, { useEffect, useMemo } from 'react';
import {
  Col, Row, Input,
  Form,
  Modal,
  notification,
  Select,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { BaseYear } from '../../sharedTypes/BaseYear';

import './BaseYearExercises.scss';
import {
  post, put, RequestWrapper, isSuperAgentError, SuperAgentError,
} from '../../dataSource/requests';

interface IModalForm {
    open: boolean;
    modalStatusChange: React.Dispatch<React.SetStateAction<boolean>>;
    baseYearEdit: undefined| BaseYear
    updateTable:()=> Promise<void>
  }

const ModalForm = ({
  open, modalStatusChange, baseYearEdit, updateTable,
}:IModalForm): JSX.Element => {
  const [formInstance] = Form.useForm<BaseYear>();
  const navigate = useNavigate();

  // eslint-disable-next-line max-len
  const createBaseYear = async (values:BaseYear):Promise<RequestWrapper | SuperAgentError | Error> => {
    try {
      const requestBack: RequestWrapper = post({
        url: '/ir-base-year',
        params: { ...values },

        frontConfigs: {
          navigateFunction: navigate,
        },
      });
      return await requestBack;
    } catch (error) {
      if (isSuperAgentError(error)) {
        return error.response;
      }
      return error as Error;
    }
  };
  // eslint-disable-next-line max-len
  const editBaseYear = async (values:BaseYear): Promise<RequestWrapper | SuperAgentError | Error> => {
    try {
      const requestBack: RequestWrapper = put({
        url: `/ir-base-year/${baseYearEdit?.id}`,
        params: { ...values },
        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      return await requestBack;
    } catch (error) {
      if (isSuperAgentError(error)) {
        return error.response;
      }
      return error as Error;
    }
  };

  const onSubmitForm = async (values:BaseYear): Promise<void> => {
    let response = { } as Promise<RequestWrapper | SuperAgentError | Error> | any;

    if (baseYearEdit?.id) {
      response = await editBaseYear(values);
    } else {
      response = await createBaseYear(values);
    }

    switch (true) {
      case (response != null && response?.body?.code === 22001): {
        notification.success({
          message: 'Sucesso',
          description: 'Novo ano base cadastrado!',
        });
        modalStatusChange(false);
        updateTable();
        break;
      }

      case (response != null && response?.body?.code === 22003): {
        notification.success({
          message: 'Sucesso',
          description: 'Ano base atualizado!',
        });
        modalStatusChange(false);
        updateTable();
        break;
      }

      case (response != null && response?.body?.code === -24001): {
        notification.error({
          message: 'Erro',
          description: 'Ano base já está cadastrado!',
        });
        modalStatusChange(false);
        break;
      }

      case (response != null && response?.body?.code === -24002): {
        notification.error({
          message: 'Erro',
          description: 'Ano base não pode ser negativo!',
        });
        modalStatusChange(false);
        break;
      }

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro, por favor tente novamente mais tarde.',
        });
        break;
    }
  };

  useEffect(() => {
    if (baseYearEdit?.id) {
      formInstance.setFieldsValue(baseYearEdit);
    } else {
      formInstance.setFieldsValue({ id: 0, baseYear: '', status: 'ACTIVE' });
    }
  }, [baseYearEdit, formInstance]);

  return (
    <Form
      scrollToFirstError
      layout="vertical"
      form={formInstance}
      className="BaseYearExercises"
      name="form"
    >
      <Modal
        visible={open}
        onOk={():void => {
          formInstance
            .validateFields()
            .then((values) => {
              formInstance.resetFields();
              onSubmitForm(values);
            })
            .catch(() => {
              notification.error({
                message: 'Erro',
                description: 'Desculpe, existe erros no formulário!',
              });
            });
        }}
        onCancel={(): void => {
          modalStatusChange(false);
          formInstance.setFieldsValue({ id: 0, baseYear: '', status: 'ACTIVE' });
        }}
      >
        <Row
          gutter={24}
          className="filters-container"
        >
          <Col xs={{ span: 24 }} lg={{ span: 16 }}>
            <Form.Item
              label="Exercício/ano-base "
              name="baseYear"
              rules={[
                { required: true },
                {
                  validator: (_, value):Promise<void> => {
                    if (value) {
                      const newValue = Number(value);

                      if (!newValue || newValue < 0) {
                        return Promise.reject(new Error('O ano base é inválido!'));
                      }
                      return Promise.resolve();
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                style={{ width: '100%' }}
                placeholder="Digite o ano"
                maxLength={4}
              />
            </Form.Item>
          </Col>
          { baseYearEdit?.id && (
          <Col xs={{ span: 24 }} lg={{ span: 6 }}>
            <Form.Item
              label="Status"
              name="status"
              rules={[]}
            >
              <Select
                showSearch
                allowClear
                style={{ width: '100%' }}
                placeholder="Selecione o status"
              >
                <Select.Option value="ACTIVE">
                  Ativo
                </Select.Option>
                <Select.Option value="INACTIVE">
                  Inativo
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          )}
        </Row>
      </Modal>
    </Form>
  );
};

export default ModalForm;
